import { Injectable } from '@angular/core';
import {
    US_L1_HQ_LEVELS,
    US_L2_HQ_LEVELS,
    EU_L1_HQ_LEVELS,
    EU_L2_HQ_LEVELS,
    EU_L3_HQ_LEVELS,
} from '../constant/constant';
import { AuthService } from './auth.service';

/**
 * Layout URL prefix pattern before `/rmm/...` (routes33).
 * US prefixes: standard, sbs-hq, sbs-branch, third-party-hq, third-party-division.
 * EU prefixes: eu-region (`/{regionName}/rmm`), eu-dealer (`/{regionName}/{dealerNumber}/rmm`).
 */
export type RmmUrlPrefixType =
    | 'standard'
    | 'sbs-hq'
    | 'sbs-branch'
    | 'third-party-hq'
    | 'third-party-division'
    | 'eu-region'
    | 'eu-dealer';

export interface ParsedRmmUrl {
    prefixType: RmmUrlPrefixType;
    /** Path segment(s) after `/rmm/`, without leading slash. */
    rmmPath: string;
    hasTenantContext: boolean;
    /** URL prefix before `/rmm` (e.g. `/sbs/88000088/281083`). */
    urlPrefix: string;
    queryString: string;
}

export interface RmmHierarchyAccessResult {
    allowed: boolean;
    redirectUrl: string;
}

/**
 * Org-level RMM route access rules.
 * Phase 1: SIICA HQ and SECL HQ (US L1).
 * Phase 1b: Tenant-only users (no hierarchy org level, e.g. IT Main) — block hierarchy URLs.
 * Phase 2: SBS HQ (US L2) — HQ/branch hierarchy + tenant routes after switchTenant.
 * Phase 3: SBS Branch (US L3) — branch prefix hierarchy + tenant routes after switchTenant.
 * Phase 4: Dealer HQ (US L2 third-party) — HQ/division hierarchy + tenant routes after switchTenant.
 * Phase 5: Division (US L3 third-party) — division hierarchy on dealer prefix + tenant routes after switchTenant.
 * Phase 6: SSA (EU L1) — region hierarchy on standard/eu-region/eu-dealer prefixes + dealer drill.
 * Phase 7: SA (EU L2) — region hierarchy on eu-region prefix + dealer drill; US/standard prefixes blocked.
 * Phase 8: DA (EU L3) — dealer hierarchy on eu-dealer prefix only + tenant routes after switchTenant.
 */
@Injectable({
    providedIn: 'root',
})
export class RmmHierarchyAccessService {

    /** Tenant routes blocked for US L1 org regardless of role. */
    private readonly l1TenantRestrictedRoutes = [
        'apply-email-notification',
        'ceo-juice',
    ];

    /**
     * SBS HQ settings/system hierarchy routes:
     * - SIICA/SECL: blocked on drill-down
     * - SBSHQ: Channel Admin / Service Main only (matches sbsHQ sidebar)
     */
    private readonly sbshqSettingsHierarchyRoutes = [
        'sbshq/supply-email-notification',
        'sbshq/operationLogsForHq',
    ];

    /** Tenant routes blocked for SBS HQ org regardless of role. */
    private readonly l2SbshqTenantRestrictedRoutes = [
        'ceo-juice',
        'tenant-access',
        'evatic',
        'ftpserver',
        'devices/firmware-update-EU',
    ];

    /**
     * SBS Branch settings/system hierarchy routes (matches sbsBranchSidebar):
     * - supply-email-notification, operationLogsForHq: Channel Admin / Service Main
     * - billingusagereport: Channel Admin only
     */
    private readonly sbsbranchSettingsHierarchyRoutes = [
        'sbsbranch/supply-email-notification',
        'sbsbranch/operationLogsForHq',
        'sbsbranch/billingusagereport',
    ];

    private readonly sbsbranchChannelAdminOnlyRoutes = [
        'sbsbranch/billingusagereport',
    ];

    /** EU / DA-only tenant routes blocked for SBS Branch. */
    private readonly l3SbsBranchTenantRestrictedRoutes = [
        'evatic',
        'ftpserver',
        'devices/firmware-update-EU',
    ];

    /**
     * Dealer HQ settings/system hierarchy routes (matches dealerHQ sidebar):
     * - division (Edit Division): Channel Admin only
     * - supply-email-notification, operationLogsForHq: Channel Admin / Service Main
     * - billingusagereport, bulk-upload-devices: Channel Admin only
     */
    private readonly dealerhqSettingsHierarchyRoutes = [
        'dealerhq/supply-email-notification',
        'dealerhq/operationLogsForHq',
        'dealerhq/billingusagereport',
        'dealerhq/bulk-upload-devices',
    ];

    private readonly dealerhqChannelAdminOnlyRoutes = [
        'division',
        'dealerhq/billingusagereport',
        'dealerhq/bulk-upload-devices',
    ];

    /**
     * Dealer division settings routes (matches dealerBranchSidebar):
     * - supply-email-notification, operationLogsForHq: Channel Admin / Service Main
     */
    private readonly dealerdivisionSettingsHierarchyRoutes = [
        'dealerdivision/supply-email-notification',
        'dealerdivision/operationLogsForHq',
    ];

    /** EU / DA-only tenant routes blocked for Dealer HQ. */
    private readonly l2DealerHqTenantRestrictedRoutes = [
        'evatic',
        'ftpserver',
        'devices/firmware-update-EU',
    ];

    /** EU / DA-only tenant routes blocked for Division. */
    private readonly l3DivisionTenantRestrictedRoutes = [
        'evatic',
        'ftpserver',
        'devices/firmware-update-EU',
    ];

    /** SIICA/SECL: division settings blocked on dealer HQ drill-down. */
    private readonly l1DealerHqDrillRestrictedHierarchyRoutes = [
        'division',
        'division-branch',
    ];

    // ---- EU (SSA / SA / DA) route sets ----

    /** Tenant routes blocked for SSA (EU L1) regardless of role. */
    private readonly l1SsaTenantRestrictedRoutes = [
        'apply-email-notification',
        'ceo-juice',
    ];

    /** SA-only hierarchy routes SSA must not open on region drill. */
    private readonly l1SsaRestrictedSaRegionHierarchyRoutes = [
        'bulk-upload-devices',
        'sa/operationLogsForHq',
    ];

    /** EU / DA-only tenant routes blocked for SA (EU L2). */
    private readonly l2SaTenantRestrictedRoutes = [
        'evatic',
        'ftpserver',
        'devices/firmware-update-EU',
    ];

    /** SA hierarchy routes gated to Channel Admin only. */
    private readonly saChannelAdminOnlyRoutes = [
        'bulk-upload-devices',
        'sa/operationLogsForHq',
    ];

    /** DA (`da/*`) settings hierarchy routes: Channel Admin / Service Main. */
    private readonly euDaSettingsHierarchyRoutes = [
        'da/supply-email-notification',
        'da/operationLogsForHq',
    ];

    /** EU usage report hierarchy routes gated to Channel Admin only. */
    private readonly euUsageReportChannelAdminOnlyRoutes = [
        'sa-usage_reports',
        'da-usage_reports',
        'da-l3_usage_reports',
    ];

    /**
     * Top-level hierarchy route segments (single path segment after `/rmm/`).
     * Nested hierarchy routes are matched via hierarchyPathPrefixes below.
     *
     * US inventory (rmmlayout-routing.module.ts) — SIICA/SECL drill-down scope:
     * | Level            | Routes |
     * |------------------|--------|
     * | L1 (standard)    | enterpriseOverviewDashboard |
     * | SBS HQ           | branchOverviewDashboard, sbs-dealer |
     * | SBS Branch       | multiTenantDashboard, sbs-dealer |
     * | Dealer HQ        | dealerOverviewDashboard, division, division-branch |
     * | Dealer Division  | multiTenantDashboard, division-branch |
     *
     * Nested under prefixes (sbshq/, sbsbranch/, dealerhq/, dealerdivision/):
     * - sbshq/supply-email-notification, sbshq/operationLogsForHq
     * - sbsbranch/supply-email-notification, sbsbranch/operationLogsForHq, sbsbranch/billingusagereport
     * - dealerhq/bulk-upload-devices, dealerhq/supply-email-notification,
     *   dealerhq/billingusagereport, dealerhq/operationLogsForHq
     * - dealerdivision/supply-email-notification, dealerdivision/operationLogsForHq
     *
     * EU hierarchy routes (SSA/SA/DA phase — not listed here):
     * regionDashboard, dealerDashboard, bulk-upload-devices, sa-usage_reports,
     * sa/operationLogsForHq, da/supply-email-notification, da/operationLogsForHq,
     * da-usage_reports, da-l3_usage_reports
     */
    private readonly hierarchyExactRoutes = new Set([
        'enterpriseOverviewDashboard',
        'branchOverviewDashboard',
        'dealerOverviewDashboard',
        'division',
        'multiTenantDashboard',
        'sbs-dealer',
        'division-branch',
        // EU hierarchy routes (SSA / SA / DA)
        'regionDashboard',
        'dealerDashboard',
        'bulk-upload-devices',
        'sa-usage_reports',
        'da-usage_reports',
        'da-l3_usage_reports',
    ]);

    /** Nested hierarchy routes — all child paths under these prefixes are hierarchy routes. */
    private readonly hierarchyPathPrefixes = [
        'sbshq/',
        'sbsbranch/',
        'dealerhq/',
        'dealerdivision/',
        // EU nested hierarchy prefixes
        'sa/',
        'da/',
    ];

    /** Default landing route for tenant-only users (IT Main, Primary Admin, etc.). */
    private readonly tenantDefaultRedirect = '/rmm/dashboard';

    constructor(private authService: AuthService) {}

    /**
     * Returns true when the logged-in user is SIICA HQ or SECL HQ.
     */
    isUsL1HqUser(): boolean {
        return US_L1_HQ_LEVELS.includes(this.authService.getOrganizationLevel());
    }

    /**
     * Tenant admins have no mapped hierarchy org level (empty string from token).
     * e.g. IT Main, Primary Admin on a customer tenant.
     */
    isTenantOnlyUser(): boolean {
        return !this.authService.getOrganizationLevel();
    }

    /**
     * Returns true when the logged-in user is SBS HQ.
     */
    isUsL2SbshqUser(): boolean {
        return US_L2_HQ_LEVELS.includes(this.authService.getOrganizationLevel());
    }

    isSbsBranchUser(): boolean {
        return this.authService.getOrganizationLevel() === 'SBSBranch';
    }

    isDealerHqUser(): boolean {
        return this.authService.getOrganizationLevel() === 'DealerHQ';
    }

    isDivisionUser(): boolean {
        return this.authService.getOrganizationLevel() === 'Division';
    }

    /** Returns true when the logged-in user is SSA (EU L1). */
    isEuL1SsaUser(): boolean {
        return EU_L1_HQ_LEVELS.includes(this.authService.getOrganizationLevel());
    }

    /** Returns true when the logged-in user is SA (EU L2). */
    isEuL2SaUser(): boolean {
        return EU_L2_HQ_LEVELS.includes(this.authService.getOrganizationLevel());
    }

    /** Returns true when the logged-in user is DA (EU L3). */
    isEuL3DaUser(): boolean {
        return EU_L3_HQ_LEVELS.includes(this.authService.getOrganizationLevel());
    }

    evaluateAccess(url: string): RmmHierarchyAccessResult {
        if (this.isUsL1HqUser()) {
            return this.evaluateL1Access(url);
        }

        if (this.isUsL2SbshqUser()) {
            return this.evaluateSbshqAccess(url);
        }

        if (this.isSbsBranchUser()) {
            return this.evaluateSbsBranchAccess(url);
        }

        if (this.isDealerHqUser()) {
            return this.evaluateDealerHqAccess(url);
        }

        if (this.isDivisionUser()) {
            return this.evaluateDivisionAccess(url);
        }

        if (this.isEuL1SsaUser()) {
            return this.evaluateSsaAccess(url);
        }

        if (this.isEuL2SaUser()) {
            return this.evaluateSaAccess(url);
        }

        if (this.isEuL3DaUser()) {
            return this.evaluateDaAccess(url);
        }

        if (this.isTenantOnlyUser()) {
            return this.evaluateTenantOnlyAccess(url);
        }

        // Unknown org level — allow (defense handled by other guards)
        return { allowed: true, redirectUrl: '' };
    }

    private evaluateL1Access(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: '/rmm/enterpriseOverviewDashboard',
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedSbshqHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedSbsbranchHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedDealerhqHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedDealerHqDrillHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1RestrictedDealerdivisionHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            return {
                allowed: this.isHierarchyAllowedForPrefix(parsed.prefixType, parsed.rmmPath),
                redirectUrl,
            };
        }

        if (this.hasL1DrillTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    /**
     * Tenant-only users must not open hierarchy drill-down URLs (e.g. pasted SBS HQ bookmark).
     * Redirect immediately to tenant dashboard so the hierarchy page does not flash before AuthService.
     */
    private evaluateTenantOnlyAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: this.tenantDefaultRedirect,
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl: this.tenantDefaultRedirect };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl: this.tenantDefaultRedirect };
        }

        // Standard /rmm/... tenant routes (dashboard, devices, etc.)
        if (parsed.prefixType === 'standard') {
            return { allowed: true, redirectUrl: '' };
        }

        // Drill prefix tenant routes require tenant context (sspTenantId / switchTenant)
        if (parsed.hasTenantContext) {
            return { allowed: true, redirectUrl: '' };
        }

        return { allowed: false, redirectUrl: this.tenantDefaultRedirect };
    }

    private evaluateSbshqAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: this.buildSbshqHomeUrl(),
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getSbshqRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL2SbshqRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            const hierarchyAllowed = this.isSbshqHierarchyAllowedForPrefix(
                parsed.prefixType,
                parsed.rmmPath,
            );
            if (!hierarchyAllowed) {
                return { allowed: false, redirectUrl };
            }
            if (
                this.isSbshqSettingsHierarchyRoute(parsed.rmmPath) &&
                !this.hasSbshqSettingsRole()
            ) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (parsed.prefixType === 'sbs-branch' && parsed.hasTenantContext) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    private evaluateSbsBranchAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: this.buildSbsBranchHomeUrl(),
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getSbsBranchRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL3SbsBranchRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            const hierarchyAllowed = this.isSbsBranchHierarchyAllowedForPrefix(
                parsed.prefixType,
                parsed.rmmPath,
            );
            if (!hierarchyAllowed) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForSbsbranchHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (parsed.prefixType === 'sbs-branch' && parsed.hasTenantContext) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    private evaluateDealerHqAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: this.buildDealerHqHomeUrl(),
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getDealerHqRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL2DealerHqRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            const hierarchyAllowed = this.isDealerHqHierarchyAllowedForPrefix(
                parsed.prefixType,
                parsed.rmmPath,
            );
            if (!hierarchyAllowed) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForDealerHqHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (this.hasDivisionTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    /**
     * Division org uses `/third-party/{dealer}/rmm/...` (no divisionId segment) per division-sidebar.
     * Dealer HQ drill-down bookmarks may use `/third-party/{dealer}/{divisionId}/rmm/...`.
     */
    private evaluateDivisionAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return {
                    allowed: false,
                    redirectUrl: this.buildDivisionHomeUrl(),
                };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getDivisionRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL3DivisionRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            const hierarchyAllowed = this.isDivisionHierarchyAllowedForPrefix(
                parsed.prefixType,
                parsed.rmmPath,
            );
            if (!hierarchyAllowed) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForDivisionHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (this.hasDivisionTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    /**
     * SSA (EU L1 — Sharp Europe). Home: /rmm/regionDashboard.
     * Region hierarchy on standard/eu-region prefixes, dealer drill on eu-dealer.
     * US prefixes and SA-only region routes are blocked.
     */
    private evaluateSsaAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return { allowed: false, redirectUrl: this.buildSsaHomeUrl() };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getSsaRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isUsPrefixType(parsed.prefixType)) {
            return { allowed: false, redirectUrl: this.buildSsaHomeUrl() };
        }

        if (this.isL1SsaRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (this.isL1SsaRestrictedSaRegionHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        // DA-native dealer routes (da-sidebar): da-usage_reports, da/* settings — SSA uses drill routes only.
        if (this.isDaNativeDealerHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl: this.buildSsaHomeUrl() };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            if (!this.isSsaHierarchyAllowedForPrefix(parsed.prefixType, parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForEuDaHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (this.hasEuDealerTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    /**
     * SA (EU L2 — Service Area). Home: /{regionName}/rmm/dealerDashboard.
     * Region hierarchy on eu-region prefix, dealer drill on eu-dealer.
     * US prefixes and the standard (SSA) prefix are blocked.
     */
    private evaluateSaAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return { allowed: false, redirectUrl: this.buildSaHomeUrl() };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getSaRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (this.isUsPrefixType(parsed.prefixType) || parsed.prefixType === 'standard') {
            return { allowed: false, redirectUrl: this.buildSaHomeUrl() };
        }

        if (this.isL2SaRestrictedTenantRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl };
        }

        if (
            parsed.prefixType === 'eu-region' &&
            this.isSaRegionTenantAccessRoute(parsed.rmmPath)
        ) {
            return { allowed: true, redirectUrl };
        }

        // DA-native dealer routes (da-sidebar): da-usage_reports, da/* settings — SA uses drill routes only.
        if (this.isDaNativeDealerHierarchyRoute(parsed.rmmPath)) {
            return { allowed: false, redirectUrl: this.buildSaHomeUrl() };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            if (!this.isSaHierarchyAllowedForPrefix(parsed.prefixType, parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForSaHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForEuDaHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (this.hasEuDealerTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    /**
     * DA (EU L3 — Dealer Area). Home: /{regionName}/{dealerNumber}/rmm/multiTenantDashboard.
     * Dealer hierarchy on eu-dealer prefix only; standard/eu-region/US prefixes blocked.
     */
    private evaluateDaAccess(url: string): RmmHierarchyAccessResult {
        const parsed = this.parseRmmUrl(url);
        if (!parsed) {
            if (url.includes('/rmm')) {
                return { allowed: false, redirectUrl: this.buildDaHomeUrl() };
            }
            return { allowed: true, redirectUrl: '' };
        }

        const redirectUrl = this.getDaRedirectUrl(parsed);

        if (!parsed.rmmPath) {
            return { allowed: false, redirectUrl };
        }

        if (
            this.isUsPrefixType(parsed.prefixType) ||
            parsed.prefixType === 'standard' ||
            parsed.prefixType === 'eu-region'
        ) {
            return { allowed: false, redirectUrl: this.buildDaHomeUrl() };
        }

        // DA drill sidebar uses da-usage_reports; redirect bookmarks of da-l3_usage_reports to the canonical route.
        if (
            parsed.prefixType === 'eu-dealer' &&
            this.isDaL3UsageReportRoute(parsed.rmmPath)
        ) {
            return {
                allowed: false,
                redirectUrl: this.buildDaDirectUsageReportUrl(parsed),
            };
        }

        if (this.isHierarchyRoute(parsed.rmmPath)) {
            if (!this.isDaHierarchyAllowedForPrefix(parsed.prefixType, parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            if (!this.isRoleAllowedForEuDaHierarchyRoute(parsed.rmmPath)) {
                return { allowed: false, redirectUrl };
            }
            return { allowed: true, redirectUrl };
        }

        if (this.hasEuDealerTenantContext(parsed)) {
            return { allowed: true, redirectUrl };
        }

        return { allowed: false, redirectUrl };
    }

    parseRmmUrl(url: string): ParsedRmmUrl | null {
        if (!url?.includes('/rmm')) {
            return null;
        }

        const normalized = url.replace(/^#/, '');
        const [pathOnly, queryString = ''] = normalized.split('?');
        const path = pathOnly.replace(/^\//, '');

        const sbsBranchMatch = path.match(/^sbs\/[^/]+\/[^/]+\/rmm(?:\/(.*))?$/);
        if (sbsBranchMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'sbs-branch',
                rmmPath: sbsBranchMatch[1] ?? '',
                hasTenantContext: this.hasTenantContext(queryString, 'sbs-branch'),
                urlPrefix,
                queryString,
            };
        }

        const sbsHqMatch = path.match(/^sbs\/[^/]+\/rmm(?:\/(.*))?$/);
        if (sbsHqMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'sbs-hq',
                rmmPath: sbsHqMatch[1] ?? '',
                hasTenantContext: false,
                urlPrefix,
                queryString,
            };
        }

        const thirdPartyDivisionMatch = path.match(/^third-party\/[^/]+\/[^/]+\/rmm(?:\/(.*))?$/);
        if (thirdPartyDivisionMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'third-party-division',
                rmmPath: thirdPartyDivisionMatch[1] ?? '',
                hasTenantContext: this.hasTenantContext(queryString, 'third-party-division'),
                urlPrefix,
                queryString,
            };
        }

        const thirdPartyHqMatch = path.match(/^third-party\/[^/]+\/rmm(?:\/(.*))?$/);
        if (thirdPartyHqMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'third-party-hq',
                rmmPath: thirdPartyHqMatch[1] ?? '',
                hasTenantContext: false,
                urlPrefix,
                queryString,
            };
        }

        const standardMatch = path.match(/^rmm(?:\/(.*))?$/);
        if (standardMatch) {
            return {
                prefixType: 'standard',
                rmmPath: standardMatch[1] ?? '',
                hasTenantContext: false,
                urlPrefix: '',
                queryString,
            };
        }

        // EU dealer drill: /{regionName}/{dealerNumber}/rmm/... (excludes US sbs/third-party prefixes)
        const euDealerMatch = path.match(
            /^(?!sbs\/|third-party\/)[^/]+\/[^/]+\/rmm(?:\/(.*))?$/,
        );
        if (euDealerMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'eu-dealer',
                rmmPath: euDealerMatch[1] ?? '',
                hasTenantContext: this.hasTenantContext(queryString, 'eu-dealer'),
                urlPrefix,
                queryString,
            };
        }

        // EU region: /{regionName}/rmm/... (excludes US sbs/third-party prefixes)
        const euRegionMatch = path.match(
            /^(?!sbs\/|third-party\/)[^/]+\/rmm(?:\/(.*))?$/,
        );
        if (euRegionMatch) {
            const urlPrefix = pathOnly.slice(0, pathOnly.indexOf('/rmm'));
            return {
                prefixType: 'eu-region',
                rmmPath: euRegionMatch[1] ?? '',
                hasTenantContext: false,
                urlPrefix,
                queryString,
            };
        }

        return null;
    }

    getRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (parsed.hasTenantContext) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        switch (parsed.prefixType) {
            case 'standard':
                return '/rmm/enterpriseOverviewDashboard';
            case 'sbs-hq':
                return `${parsed.urlPrefix}/rmm/branchOverviewDashboard`;
            case 'sbs-branch':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            case 'third-party-hq':
                return `${parsed.urlPrefix}/rmm/dealerOverviewDashboard`;
            case 'third-party-division':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            default:
                return '/rmm/enterpriseOverviewDashboard';
        }
    }

    isRouteBlockedForL1Bookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL1RestrictedTenantRoute(parsed.rmmPath);
    }

    isRouteBlockedForSbshqBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL2SbshqRestrictedTenantRoute(parsed.rmmPath);
    }

    isRouteBlockedForSbsBranchBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL3SbsBranchRestrictedTenantRoute(parsed.rmmPath);
    }

    isRouteBlockedForDealerHqBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL2DealerHqRestrictedTenantRoute(parsed.rmmPath);
    }

    isRouteBlockedForDivisionBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL3DivisionRestrictedTenantRoute(parsed.rmmPath);
    }

    private hasTenantContext(
        queryString: string,
        prefixType: RmmUrlPrefixType,
    ): boolean {
        const isBranchOrDivision =
            prefixType === 'sbs-branch' ||
            prefixType === 'third-party-division' ||
            prefixType === 'eu-dealer';
        if (!isBranchOrDivision) {
            return false;
        }

        const hasSspTenantId = queryString.includes('sspTenantId=');
        const hasSwitchTenant = localStorage.getItem('switchTenant') === 'true';
        return hasSspTenantId || hasSwitchTenant;
    }

    private isL1RestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(rmmPath, this.l1TenantRestrictedRoutes);
    }

    private isL1RestrictedSbshqHierarchyRoute(rmmPath: string): boolean {
        return this.isSbshqSettingsHierarchyRoute(rmmPath);
    }

    private isL1RestrictedSbsbranchHierarchyRoute(rmmPath: string): boolean {
        return this.isSbsbranchSettingsHierarchyRoute(rmmPath);
    }

    private isL1RestrictedDealerhqHierarchyRoute(rmmPath: string): boolean {
        return this.isDealerhqSettingsHierarchyRoute(rmmPath);
    }

    private isL1RestrictedDealerHqDrillHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        return this.l1DealerHqDrillRestrictedHierarchyRoutes.includes(normalizedPath);
    }

    private isL1RestrictedDealerdivisionHierarchyRoute(rmmPath: string): boolean {
        return this.isDealerdivisionSettingsHierarchyRoute(rmmPath);
    }

    private isSbshqSettingsHierarchyRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.sbshqSettingsHierarchyRoutes,
        );
    }

    private hasSbshqSettingsRole(): boolean {
        const role = this.authService.rmmInfo?.role;
        return role === 'Channel Admin' || role === 'Service Main';
    }

    private hasChannelAdminRole(): boolean {
        return this.authService.rmmInfo?.role === 'Channel Admin';
    }

    private isL3SbsBranchRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.l3SbsBranchTenantRestrictedRoutes,
        );
    }

    private isSbsbranchSettingsHierarchyRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.sbsbranchSettingsHierarchyRoutes,
        );
    }

    private isRoleAllowedForSbsbranchHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        if (this.sbsbranchChannelAdminOnlyRoutes.includes(normalizedPath)) {
            return this.hasChannelAdminRole();
        }
        if (this.isSbsbranchSettingsHierarchyRoute(rmmPath)) {
            return this.hasSbshqSettingsRole();
        }
        return true;
    }

    private isL2DealerHqRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.l2DealerHqTenantRestrictedRoutes,
        );
    }

    private isL3DivisionRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.l3DivisionTenantRestrictedRoutes,
        );
    }

    private isRoleAllowedForDivisionHierarchyRoute(rmmPath: string): boolean {
        if (this.isDealerdivisionSettingsHierarchyRoute(rmmPath)) {
            return this.hasSbshqSettingsRole();
        }
        return true;
    }

    /**
     * Tenant context for SIICA/SECL drill-down to a customer tenant under a dealer prefix.
     * Device drill from dealer overview may use `/third-party/{dealer}/rmm/...` (no division segment)
     * when auth rebuilds the URL or divisionId is omitted — requires sspTenantId or switchTenant.
     */
    private hasL1DrillTenantContext(parsed: ParsedRmmUrl): boolean {
        if (parsed.hasTenantContext) {
            return true;
        }
        return this.hasDealerPrefixTenantContext(parsed);
    }

    private hasDivisionTenantContext(parsed: ParsedRmmUrl): boolean {
        if (
            parsed.prefixType !== 'third-party-hq' &&
            parsed.prefixType !== 'third-party-division'
        ) {
            return false;
        }

        if (parsed.prefixType === 'third-party-division' && parsed.hasTenantContext) {
            return true;
        }

        return this.hasDealerPrefixTenantContext(parsed);
    }

    private hasDealerPrefixTenantContext(parsed: ParsedRmmUrl): boolean {
        const hasSspTenantId = parsed.queryString.includes('sspTenantId=');
        const hasSwitchTenant = localStorage.getItem('switchTenant') === 'true';
        return (
            parsed.prefixType === 'third-party-hq' &&
            (hasSspTenantId || hasSwitchTenant)
        );
    }

    private isDealerhqSettingsHierarchyRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.dealerhqSettingsHierarchyRoutes,
        );
    }

    private isDealerdivisionSettingsHierarchyRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.dealerdivisionSettingsHierarchyRoutes,
        );
    }

    private isRoleAllowedForDealerHqHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        if (this.dealerhqChannelAdminOnlyRoutes.includes(normalizedPath)) {
            return this.hasChannelAdminRole();
        }
        if (this.isDealerhqSettingsHierarchyRoute(rmmPath)) {
            return this.hasSbshqSettingsRole();
        }
        if (this.isDealerdivisionSettingsHierarchyRoute(rmmPath)) {
            return this.hasSbshqSettingsRole();
        }
        return true;
    }

    private isL2SbshqRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(
            rmmPath,
            this.l2SbshqTenantRestrictedRoutes,
        );
    }

    private isRestrictedTenantRoute(
        rmmPath: string,
        restrictedRoutes: string[],
    ): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        return restrictedRoutes.some(
            (route) =>
                normalizedPath === route ||
                normalizedPath.startsWith(`${route}/`),
        );
    }

    private getSbshqRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (parsed.hasTenantContext) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        switch (parsed.prefixType) {
            case 'sbs-hq':
                return `${parsed.urlPrefix}/rmm/branchOverviewDashboard`;
            case 'sbs-branch':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            default:
                return this.buildSbshqHomeUrl();
        }
    }

    private buildSbshqHomeUrl(): string {
        const hqNumber = this.authService.currentHqInfo?.L2HQ;
        if (hqNumber) {
            return `/sbs/${hqNumber}/rmm/branchOverviewDashboard`;
        }
        return '/rmm/branchOverviewDashboard';
    }

    private getSbsBranchRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (parsed.hasTenantContext) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        if (parsed.prefixType === 'sbs-branch') {
            return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
        }

        return this.buildSbsBranchHomeUrl();
    }

    private buildSbsBranchHomeUrl(): string {
        const hqNumber = this.authService.currentHqInfo?.L2HQ;
        const dealerNumber = this.authService.rmmDealerNumber;
        if (hqNumber && dealerNumber) {
            return `/sbs/${hqNumber}/${dealerNumber}/rmm/multiTenantDashboard`;
        }
        return '/rmm/dashboard';
    }

    private getDealerHqRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (parsed.hasTenantContext) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        switch (parsed.prefixType) {
            case 'third-party-hq':
                return `${parsed.urlPrefix}/rmm/dealerOverviewDashboard`;
            case 'third-party-division':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            default:
                return this.buildDealerHqHomeUrl();
        }
    }

    private buildDealerHqHomeUrl(): string {
        const dealerNumber = this.authService.rmmDealerNumber;
        if (dealerNumber) {
            return `/third-party/${dealerNumber}/rmm/dealerOverviewDashboard`;
        }
        return '/rmm/dealerOverviewDashboard';
    }

    private getDivisionRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (this.hasDivisionTenantContext(parsed)) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        if (
            parsed.prefixType === 'third-party-hq' ||
            parsed.prefixType === 'third-party-division'
        ) {
            return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
        }

        return this.buildDivisionHomeUrl();
    }

    private buildDivisionHomeUrl(): string {
        const dealerNumber = this.authService.rmmDealerNumber;
        if (dealerNumber) {
            return `/third-party/${dealerNumber}/rmm/multiTenantDashboard`;
        }
        return '/rmm/multiTenantDashboard';
    }

    private isHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        if (this.hierarchyExactRoutes.has(normalizedPath)) {
            return true;
        }
        return this.hierarchyPathPrefixes.some((prefix) =>
            normalizedPath.startsWith(prefix),
        );
    }

    private isHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        const normalizedPath = rmmPath.split('?')[0];

        switch (prefixType) {
            case 'standard':
                return normalizedPath === 'enterpriseOverviewDashboard';
            case 'sbs-hq':
                return (
                    normalizedPath === 'branchOverviewDashboard' ||
                    normalizedPath === 'sbs-dealer' ||
                    normalizedPath.startsWith('sbshq/')
                );
            case 'sbs-branch':
                return (
                    normalizedPath === 'multiTenantDashboard' ||
                    normalizedPath === 'sbs-dealer' ||
                    normalizedPath.startsWith('sbsbranch/')
                );
            case 'third-party-hq':
                return (
                    normalizedPath === 'dealerOverviewDashboard' ||
                    normalizedPath === 'division' ||
                    normalizedPath === 'division-branch' ||
                    normalizedPath.startsWith('dealerhq/')
                );
            case 'third-party-division':
                return (
                    normalizedPath === 'multiTenantDashboard' ||
                    normalizedPath === 'division-branch' ||
                    normalizedPath.startsWith('dealerdivision/')
                );
            default:
                return false;
        }
    }

    private isSbshqHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        if (prefixType !== 'sbs-hq' && prefixType !== 'sbs-branch') {
            return false;
        }
        return this.isHierarchyAllowedForPrefix(prefixType, rmmPath);
    }

    private isSbsBranchHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        if (prefixType !== 'sbs-branch') {
            return false;
        }
        return this.isHierarchyAllowedForPrefix(prefixType, rmmPath);
    }

    private isDealerHqHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        if (prefixType !== 'third-party-hq' && prefixType !== 'third-party-division') {
            return false;
        }
        return this.isHierarchyAllowedForPrefix(prefixType, rmmPath);
    }

    private isDivisionHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        const normalizedPath = rmmPath.split('?')[0];

        if (prefixType === 'third-party-hq') {
            return (
                normalizedPath === 'multiTenantDashboard' ||
                normalizedPath === 'division-branch' ||
                normalizedPath.startsWith('dealerdivision/')
            );
        }

        if (prefixType === 'third-party-division') {
            return (
                normalizedPath === 'multiTenantDashboard' ||
                normalizedPath === 'division-branch' ||
                normalizedPath.startsWith('dealerdivision/')
            );
        }

        return false;
    }

    // ---- EU (SSA / SA / DA) helpers ----

    /** True for US layout prefixes — EU users must not open these. */
    private isUsPrefixType(prefixType: RmmUrlPrefixType): boolean {
        return (
            prefixType === 'sbs-hq' ||
            prefixType === 'sbs-branch' ||
            prefixType === 'third-party-hq' ||
            prefixType === 'third-party-division'
        );
    }

    isRouteBlockedForSsaBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL1SsaRestrictedTenantRoute(parsed.rmmPath);
    }

    isRouteBlockedForSaBookmark(url: string): boolean {
        const parsed = this.parseRmmUrl(url);
        if (!parsed?.rmmPath) {
            return false;
        }
        return this.isL2SaRestrictedTenantRoute(parsed.rmmPath);
    }

    private isL1SsaRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(rmmPath, this.l1SsaTenantRestrictedRoutes);
    }

    private isL1SsaRestrictedSaRegionHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        return this.l1SsaRestrictedSaRegionHierarchyRoutes.includes(normalizedPath);
    }

    private isL2SaRestrictedTenantRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(rmmPath, this.l2SaTenantRestrictedRoutes);
    }

    /** SA-specific tenant route accessible at region level (Tenant Access page). */
    private isSaRegionTenantAccessRoute(rmmPath: string): boolean {
        return rmmPath.split('?')[0] === 'tenant-access';
    }

    private isEuDaSettingsHierarchyRoute(rmmPath: string): boolean {
        return this.isRestrictedTenantRoute(rmmPath, this.euDaSettingsHierarchyRoutes);
    }

    /** Channel Admin for EU usage reports; Channel Admin / Service Main for da/* settings. */
    private isRoleAllowedForEuDaHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        if (this.euUsageReportChannelAdminOnlyRoutes.includes(normalizedPath)) {
            return this.hasChannelAdminRole();
        }
        if (this.isEuDaSettingsHierarchyRoute(rmmPath)) {
            return this.hasSbshqSettingsRole();
        }
        return true;
    }

    /** Channel Admin only for SA bulk upload and region operation logs. */
    private isRoleAllowedForSaHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        if (this.saChannelAdminOnlyRoutes.includes(normalizedPath)) {
            return this.hasChannelAdminRole();
        }
        return true;
    }

    private isSsaHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        const normalizedPath = rmmPath.split('?')[0];

        if (prefixType === 'standard') {
            return (
                normalizedPath === 'regionDashboard' ||
                normalizedPath === 'sa-usage_reports'
            );
        }

        if (prefixType === 'eu-region') {
            return (
                normalizedPath === 'dealerDashboard' ||
                normalizedPath === 'sa-usage_reports'
            );
        }

        if (prefixType === 'eu-dealer') {
            return (
                normalizedPath === 'multiTenantDashboard' ||
                normalizedPath === 'da-l3_usage_reports'
            );
        }

        return false;
    }

    private isSaHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        const normalizedPath = rmmPath.split('?')[0];

        if (prefixType === 'eu-region') {
            return (
                normalizedPath === 'dealerDashboard' ||
                normalizedPath === 'bulk-upload-devices' ||
                normalizedPath === 'sa-usage_reports' ||
                normalizedPath === 'sa/operationLogsForHq'
            );
        }

        if (prefixType === 'eu-dealer') {
            return (
                normalizedPath === 'multiTenantDashboard' ||
                normalizedPath === 'da-l3_usage_reports'
            );
        }

        return false;
    }

    private isDaHierarchyAllowedForPrefix(
        prefixType: RmmUrlPrefixType,
        rmmPath: string,
    ): boolean {
        if (prefixType !== 'eu-dealer') {
            return false;
        }
        const normalizedPath = rmmPath.split('?')[0];
        return (
            normalizedPath === 'multiTenantDashboard' ||
            normalizedPath === 'da-usage_reports' ||
            normalizedPath.startsWith('da/')
        );
    }

    /** DA-native usage report route (da-sidebar); not for SSA/SA drill-down. */
    private isDaDirectUsageReportRoute(rmmPath: string): boolean {
        return rmmPath.split('?')[0] === 'da-usage_reports';
    }

    /**
     * DA-native dealer hierarchy routes linked from da-sidebar (not l3-side-bar).
     * SSA/SA must not access via bookmark — use drill routes (e.g. da-l3_usage_reports) instead.
     */
    private isDaNativeDealerHierarchyRoute(rmmPath: string): boolean {
        const normalizedPath = rmmPath.split('?')[0];
        return (
            normalizedPath === 'da-usage_reports' || normalizedPath.startsWith('da/')
        );
    }

    /** SSA/SA drill-down usage report route (l3-side-bar). */
    private isDaL3UsageReportRoute(rmmPath: string): boolean {
        return rmmPath.split('?')[0] === 'da-l3_usage_reports';
    }

    private buildDaDirectUsageReportUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';
        return `${parsed.urlPrefix}/rmm/da-usage_reports${query}`;
    }

    /** Tenant context for EU dealer drill (eu-dealer prefix + sspTenantId / switchTenant). */
    private hasEuDealerTenantContext(parsed: ParsedRmmUrl): boolean {
        if (parsed.prefixType !== 'eu-dealer') {
            return false;
        }
        const hasSspTenantId = parsed.queryString.includes('sspTenantId=');
        const hasSwitchTenant = localStorage.getItem('switchTenant') === 'true';
        return hasSspTenantId || hasSwitchTenant;
    }

    private getSsaRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (this.hasEuDealerTenantContext(parsed)) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        switch (parsed.prefixType) {
            case 'eu-region':
                return `${parsed.urlPrefix}/rmm/dealerDashboard`;
            case 'eu-dealer':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            default:
                return this.buildSsaHomeUrl();
        }
    }

    private getSaRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (this.hasEuDealerTenantContext(parsed)) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        switch (parsed.prefixType) {
            case 'eu-region':
                return `${parsed.urlPrefix}/rmm/dealerDashboard`;
            case 'eu-dealer':
                return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
            default:
                return this.buildSaHomeUrl();
        }
    }

    private getDaRedirectUrl(parsed: ParsedRmmUrl): string {
        const query = parsed.queryString ? `?${parsed.queryString}` : '';

        if (this.hasEuDealerTenantContext(parsed)) {
            return `${parsed.urlPrefix}/rmm/dashboard${query}`;
        }

        if (parsed.prefixType === 'eu-dealer') {
            return `${parsed.urlPrefix}/rmm/multiTenantDashboard${query}`;
        }

        return this.buildDaHomeUrl();
    }

    private buildSsaHomeUrl(): string {
        return '/rmm/regionDashboard';
    }

    private buildSaHomeUrl(): string {
        const regionName = this.authService.regionName;
        if (regionName) {
            return `/${regionName}/rmm/dealerDashboard`;
        }
        return '/rmm/dealerDashboard';
    }

    private buildDaHomeUrl(): string {
        const regionName = this.authService.regionName;
        const dealerNumber = this.authService.rmmDealerNumber;
        if (regionName && dealerNumber) {
            return `/${regionName}/${dealerNumber}/rmm/multiTenantDashboard`;
        }
        return '/rmm/multiTenantDashboard';
    }
}
